import React, { useEffect } from 'react';
import { useCJSeancesDay } from 'containers/CahierJournal/queries/useCJSeancesDay';
import { format } from '@teetsh/app/src/utils/time/format';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import tw from 'twin.macro';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { doesDayBelongToWorkingDays } from '@teetsh/app/src/containers/CahierJournal/utils/doesDayBelongToWorkingDays';
import { GetEventsButton } from './GetEventsButton';
import { ClassroomColumn } from '@teetsh/app/src/containers/CahierJournal/DayColumn/ClassroomColumn';
import { Header as CahierJournalHeader } from 'containers/CahierJournal/DayColumn/Header';
import { AnnualEvents } from 'containers/CahierJournal/DayColumn/AnnualEvents';
import { useCJConfigurationDisplay } from '@teetsh/app/src/containers/CahierJournal/useCJConfigurationDisplay';
import { getClassroomsWithEvents } from './getClassroomsWithEvents';
import { useCollapsedDays, useToggleCollapsedDay } from '../atoms';
import { isoDatePatternWithSlashes } from 'utils/time/datePatterns';
import { CahierJournalView } from 'containers/CahierJournal/types';
import { useIsBelowBreakpoint } from 'components/ResponsiveSwitcher/useIsBelowSize';
import { PlannerEvents } from 'containers/CahierJournal/DayColumn/PlannerEvents';
import { ReunionWithCategory } from 'types/reunions';
import { Eleve } from 'types/eleves';
import { BirthDays } from 'containers/CahierJournal/DayColumn/Birthdays';
import { getRandomInt } from 'utils/getRandomInt';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollConfig } from '@teetsh/app/src/containers/CahierJournal/hooks/useScrollConfig';

interface Props {
  isFetched: boolean;
  day: Date;
  isWeekFetched: boolean;
  view: CahierJournalView;
  plannerEvents: ReunionWithCategory[];
  eleves: Eleve[];
  previewLink: string;
  hideColumnContent?: boolean;
  disableHeaderActions?: boolean;
}

export function DayColumn(props: Props) {
  const isMobile = useIsBelowBreakpoint('md');
  const {
    day,
    isWeekFetched,
    isFetched,
    view,
    plannerEvents,
    hideColumnContent,
    disableHeaderActions,
  } = props;
  const dayString = format(day, 'fr-FR');
  const { school, schoolyear } = useSchoolSchoolyear();
  const { expanded, status, classroomsId } = useCJConfigurationDisplay();
  const { globalScroll } = useScrollConfig(view);
  const toggleCollapsedDay = useToggleCollapsedDay();
  const collapsedDays = useCollapsedDays();
  const { t } = useTranslation(['cahierJournal']);

  const allClassrooms = useClassrooms();
  const classroomsOfTheDays = allClassrooms.filter(
    c =>
      (classroomsId.includes(c.id) || classroomsId.length === 0) &&
      doesDayBelongToWorkingDays(day, c.workingDays),
  );

  useEffect(() => {
    (window as any).printUrl = `/print/annee-scolaire/${schoolyear.id}/ecole/${
      school.id
    }/cahier-journal/day/${format(day, 'fr-FR', isoDatePatternWithSlashes)}`;
  }, []);
  const { data: events } = useCJSeancesDay({
    day: dayString,
    enabled: isWeekFetched,
  });

  const classroomsWithEvents = getClassroomsWithEvents(
    classroomsOfTheDays,
    events || [],
    status,
    day,
    school.classroomsOrg,
  );
  const classroomsWorkingThisDay = allClassrooms.filter(c =>
    doesDayBelongToWorkingDays(day, c.workingDays),
  );

  const [params, setParams] = useSearchParams();
  const highlightedEventIds = params.get('highlight')?.split(',') || [];
  const firstEventId = highlightedEventIds[0];
  useEffect(() => {
    if (firstEventId) {
      document
        .getElementById(`seance-${firstEventId}`)
        ?.scrollIntoView({ behavior: 'smooth' });

      setTimeout(() => {
        params.delete('highlight');
        setParams(params, { replace: true });
      }, 6000);
    }
  }, [events]);

  // Les h-full sont importants pour safari see : https://stackoverflow.com/questions/33636796/chrome-safari-not-filling-100-height-of-flex-parent
  return (
    <div
      // see : https://css-tricks.com/flexbox-truncated-text/
      css={[
        tw`flex flex-col max-h-full flex-1`,
        !expanded && tw`min-w-0`,

        // view === 'day' && tw`px-2 md:px-4`,
        // view !== 'day' && tw` px-2 md:px-0`,
      ]}
      key={dayString}
    >
      {view !== 'day' && (
        <CahierJournalHeader
          setCollapse={() => toggleCollapsedDay(dayString)}
          isCollapsed={collapsedDays.includes(dayString)}
          previewLink={props.previewLink}
          view={view}
          day={day}
          tw="mb-2"
          disabled={disableHeaderActions}
        />
      )}
      {!hideColumnContent && (
        <div
          key={dayString}
          css={[
            !globalScroll && tw`min-h-0 h-full overflow-y-auto`,
            tw`flex-grow flex max-h-full flex-col`,
          ]}
        >
          <AnnualEvents tw="mb-2" day={day} />
          <BirthDays tw="mb-2" day={day} eleves={props.eleves} />
          <PlannerEvents tw="mb-2" day={day} plannerEvents={plannerEvents} />
          <div
            css={[
              tw`flex-auto relative min-w-0`,
              !globalScroll && tw`h-full min-h-0`,
            ]}
          >
            <div tw="flex flex-col bg-white rounded-lg px-3 py-4 mb-0.5">
              {isFetched ? (
                <>
                  <div
                    css={[
                      tw`flex min-h-0 min-w-0 max-h-full`,
                      view === 'day' ? tw`space-x-4` : tw`space-x-2`,
                    ]}
                  >
                    {classroomsWithEvents.length === 0 && (
                      <div tw="text-neutral-quiet text-sm text-center">
                        {t('cahierJournal:dayColumn.classroomsFiltered', {
                          count: classroomsWorkingThisDay.length,
                          classrooms: classroomsWorkingThisDay
                            .map(v => v.name)
                            .join(', '),
                        })}
                      </div>
                    )}
                    {classroomsWithEvents.map(classroom => (
                      <ClassroomColumn
                        isMobile={isMobile}
                        expanded={expanded}
                        day={day}
                        classroom={classroom}
                        key={`${classroom.id} %% ${dayString}`}
                        classroomsOrg={school.classroomsOrg}
                        classrooms={classroomsOfTheDays}
                        collapsed={collapsedDays.includes(dayString)}
                        highlightedEventIds={highlightedEventIds}
                      />
                    ))}
                  </div>
                  {classroomsWithEvents.length > 0 &&
                    (events || []).length === 0 && (
                      <div tw="shrink-0 mt-2">
                        <GetEventsButton
                          classrooms={classroomsOfTheDays}
                          date={day}
                        />
                      </div>
                    )}
                </>
              ) : (
                Array.from({ length: getRandomInt(3) + 1 }, (_, i) => (
                  <div
                    key={i}
                    tw="h-14 mb-2 w-full rounded-lg"
                    className="tsh-skeleton"
                  />
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
